import { Checkbox, Chip, TableCell, TableRow } from "@mui/material";
import React from "react";

import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import {
  images,
  truncateString,
  showNotification,
} from "../../../../constants";
import CancelIcon from '@mui/icons-material/Cancel';
import StatusDropdown from "../../../../common/StatusDropdown";
import moment from "moment";
import { updateQuickTale } from "../../../OrganizationQuickTales/hooks/QuickTalesHooks";
import { useAuth } from "../../../../hooks";
import { Toast } from "../../../../common/alert";
import { useQuickTalesContext } from "../../../../providers/OrganizationAdmin/QuickTalesProvider";

// Reusable row component that shows QT Org Status (approvalStatus.status) next to the title.
// Used in AddQuickTalesToOrganization and Organization Batches tables.
const AddQuickTalesToOrgTableRow = ({
  isItemSelected,
  row,
  numberType,
  handleClickRow,
  labelId,
  handleImagePreviewModalOpen,
  handleAudioModalOpen,
  handleNavigateToDetails,
  setSelectedImage,
  setSelectedAudioFile,
  columnWidths,
  isBatchMode = false,
  onUpdateApprovalStatus,
}) => {
  const { currentUser } = useAuth();

  const { handleGetAllQuickTales, loading, setLoading } =
    useQuickTalesContext();

  const handleUpdateQuickTaleStatus = async (status) => {
    // If a custom handler is provided (e.g., BatchTableContainer), delegate to it
    if (onUpdateApprovalStatus) {
      return onUpdateApprovalStatus(status, row.id);
    }

    try {
      setLoading(true);
      let dataTemp = {
        approvalStatus: {
          status: status,
          updatedAt: new Date(),
          updatedBy: currentUser?.userName,
        },
      };
      console.log(status, row.id);
      let responseTemp = await updateQuickTale(row.id, dataTemp);
      console.log("update quicktale response:", responseTemp);
      await handleGetAllQuickTales();
      showNotification("QuickTale updated successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating quicktale", error.message);
    } finally {
      setLoading(false);
    }
  };

  const dropdownOptions = ["approved", "pending", "denied"];

  const isApproved = row?.approvalStatus?.status === "approved";
  const isLocked = isBatchMode && isApproved;

  return (
    <TableRow
      hover
      // onClick={(event) => handleClickRow(event, row.id)}
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
      sx={{
        cursor: "pointer",
        height: 48,
        background: numberType === true ? "#F2F2F2" : "#fff",
      }}
    >
      <TableCell
        padding="checkbox"
        style={{ background: "#fff" }}
        onClick={(event) => !isLocked && handleClickRow(event, row.id)}
      >
        <Checkbox
          color="primary"
          checked={isItemSelected}
          disabled={isLocked}
          inputProps={{
            "aria-labelledby": labelId,
          }}
        />
      </TableCell>
      <TableCell
        align="left"
        style={{
          ...tableCellStyles,
          width: columnWidths?.csvRow || 90,
          minWidth: columnWidths?.csvRow || 90,
          maxWidth: columnWidths?.csvRow || 90,
        }}
        onClick={handleNavigateToDetails}
        title={row?.csvRow}
      >
        {typeof row?.csvRow === "number" ? row.csvRow : ""}
      </TableCell>
      <TableCell
        // component="th"
        id={labelId}
        scope="row"
        // padding="none"
        onClick={handleNavigateToDetails}
        style={{
          ...tableTitleFieldStyles,
          width: columnWidths?.title || 300,
          minWidth: columnWidths?.title || 300,
          maxWidth: columnWidths?.title || 300,
        }}
        title={row?.title}
      >
        {row?.title && truncateString(row?.title, 40)}
      </TableCell>
      <TableCell
        align="left"
        style={{
          ...tableCellStyles,
          width: columnWidths?.["approvalStatus.status"] || 170,
          minWidth: columnWidths?.["approvalStatus.status"] || 170,
          maxWidth: columnWidths?.["approvalStatus.status"] || 170,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <StatusDropdown
          options={dropdownOptions}
          currentValue={row?.approvalStatus?.status}
          onChange={handleUpdateQuickTaleStatus}
          disabled={isLocked}
        />
      </TableCell>
      <TableCell 
        align="left" 
        style={{
          ...tableCellStyles,
          width: columnWidths?.isDraft || 110,
          minWidth: columnWidths?.isDraft || 110,
          maxWidth: columnWidths?.isDraft || 110,
        }}
      >
        {row.isDraft === true ? "Draft" : "Finished"}
      </TableCell>
      <TableCell
        align="center"
        style={{
          border: "1px solid #BABABA",
          width: columnWidths?.photo || 90,
          minWidth: columnWidths?.photo || 90,
          maxWidth: columnWidths?.photo || 90,
        }}
        onClick={() => {
          setSelectedImage(
            row?.photoUrl ? row?.photoUrl : images.QuicktaleLogo
          );
          handleImagePreviewModalOpen();
        }}
      >
        <img
          // src={images.tableDummyImage}
          src={row?.photoUrl ? row?.photoUrl : images.QuicktaleLogo}
          alt="table image"
          style={{ width: 45, height: 40 }}
        />
      </TableCell>
      <TableCell
        align="center"
        style={{
          border: "1px solid #BABABA",
          width: columnWidths?.audio || 90,
          minWidth: columnWidths?.audio || 90,
          maxWidth: columnWidths?.audio || 90,
        }}
        onClick={() => {
          if (row?.processedAudioUrl || row?.audioUrl) {
            setSelectedAudioFile(row?.processedAudioUrl || row?.audioUrl);
            handleAudioModalOpen();
          }
        }}
      >
        {(row?.processedAudioUrl || row?.audioUrl) ? (
          <PlayCircleFilledWhiteOutlinedIcon
            fontSize="large"
            style={{ color: "#00A7CD" }}
          />
        ) : (
          <CancelIcon
            fontSize="large"
            style={{ color: "#D32F2F" }}
          />
        )}
      </TableCell>
      <TableCell
        align="left"
        style={{
          ...tableCellStyles,
          width: columnWidths?.areaIdResolved || 170,
          minWidth: columnWidths?.areaIdResolved || 170,
          maxWidth: columnWidths?.areaIdResolved || 170,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={row?.areaNameResolved || ""}
      >
        {row?.areaNameResolved || ""}
      </TableCell>
      <TableCell 
        align="left" 
        style={{
          ...tableCellStyles,
          width: columnWidths?.["narratorData.userName"] || 170,
          minWidth: columnWidths?.["narratorData.userName"] || 170,
          maxWidth: columnWidths?.["narratorData.userName"] || 170,
        }}
      >
        {row.narratorData ? row.narratorData?.userName : row.narrator}
      </TableCell>
      <TableCell
        align="left"
        style={{ 
          ...tableCellStyles, 
          textTransform: "capitalize",
          width: columnWidths?.status || 150,
          minWidth: columnWidths?.status || 150,
          maxWidth: columnWidths?.status || 150,
        }}
      >
        {row.status}
      </TableCell>
      <TableCell 
        align="left" 
        style={{
          ...tableCellStyles,
          width: columnWidths?.lastEmailSentAt || 180,
          minWidth: columnWidths?.lastEmailSentAt || 180,
          maxWidth: columnWidths?.lastEmailSentAt || 180,
        }}
      >
        {row?.lastEmailSentAt?.seconds
          ? moment.unix(row.lastEmailSentAt.seconds).format("MM/DD/YYYY hh:mm:ss A")
          : ""}
      </TableCell>
      <TableCell 
        align="center" 
        style={{
          ...tableCellStyles,
          width: columnWidths?.lastEmailStatus || 140,
          minWidth: columnWidths?.lastEmailStatus || 140,
          maxWidth: columnWidths?.lastEmailStatus || 140,
        }}
      >
        {row?.lastEmailStatus ? (() => {
          const statusConfig = {
            sent: { label: "Sent", color: "default" },
            processed: { label: "Processed", color: "default" },
            deferred: { label: "Deferred", color: "warning" },
            delivered: { label: "Delivered", color: "success" },
            opened: { label: "Opened", color: "success" },
            bounced: { label: "Bounced", color: "error" },
            dropped: { label: "Dropped", color: "error" },
            spam_report: { label: "Spam", color: "error" },
          };
          const config = statusConfig[row.lastEmailStatus] || { label: row.lastEmailStatus, color: "default" };
          return (
            <Chip
              label={config.label}
              size="small"
              color={config.color}
              sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 12 }}
            />
          );
        })() : ""}
      </TableCell>
      <TableCell 
        align="left" 
        style={{
          ...tableCellStyles,
          width: columnWidths?.updatedAt || 180,
          minWidth: columnWidths?.updatedAt || 180,
          maxWidth: columnWidths?.updatedAt || 180,
        }}
      >
        {row?.updatedAt &&
          moment
            .unix(row?.updatedAt.seconds)
            .format("MM/DD/YYYY hh:mm:ss A")}
      </TableCell>
      <TableCell 
        align="left" 
        style={{
          ...tableCellStyles,
          width: columnWidths?.createdBy || 130,
          minWidth: columnWidths?.createdBy || 100,
          maxWidth: columnWidths?.createdBy || 180,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row?.createdByData && `${row?.createdByData?.firstName + " " + row?.createdByData?.lastName}`}
      </TableCell>
      <TableCell 
        align="left" 
        style={{
          ...tableCellStyles,
          width: columnWidths?.["approvalStatus.updatedAt"] || 180,
          minWidth: columnWidths?.["approvalStatus.updatedAt"] || 180,
          maxWidth: columnWidths?.["approvalStatus.updatedAt"] || 180,
        }}
      >
        {/*date modified*/}
        {row?.approvalStatus?.updatedAt &&
          moment
            .unix(row?.approvalStatus?.updatedAt.seconds)
            .format("MM/DD/YYYY hh:mm:ss A")}
      </TableCell>
      <TableCell
        align="center"
        style={{
          ...tableCellStyles,
          width: columnWidths?.autoApprove || 120,
          minWidth: columnWidths?.autoApprove || 120,
          maxWidth: columnWidths?.autoApprove || 120,
        }}
      >
        <Checkbox
          checked={row?.autoApprove === true}
          disabled
          size="small"
          sx={{
            color: "#00A7CD",
            "&.Mui-disabled": {
              color: row?.autoApprove === true ? "#00A7CD" : undefined,
            },
            "&.Mui-checked.Mui-disabled": {
              color: "#00A7CD",
            },
          }}
        />
      </TableCell>

    </TableRow>
  );
};

export default AddQuickTalesToOrgTableRow;

const tableTitleFieldStyles = {
  // paddingLeft: 5,
  // fontWeight: 600,
  border: "1px solid #BABABA",
  color: "#00A7CD",
  cursor: "pointer",
  fontFamily: "Avenir Book, sans-serif",
};

const tableCellStyles = {
  border: "1px solid #BABABA",
  cursor: "default",
  fontFamily: "Avenir Book, sans-serif",
};
const tableStatusCellStyles = {
  border: "1px solid #BABABA",
  cursor: "pointer",
  // fontWeight: 600,
  fontFamily: "Avenir Book, sans-serif",
};
